import { startLoading, stopLoading } from "../common/utils/button-loading";
import {
	SaveInlineEditingData,
	SaveInlineEditingResponse,
} from "./interfaces/http";
import { saveInlineEditing } from "./services/inline-editing-service";
import closeEditMode from "./utils/close-edit-mode";
import { exitAllEditModesExcept } from "./utils/exit-edit-mode";
import openEditMode from "./utils/open-edit-mode";

declare var pm: PmInterface;

export default function setupInlineEditing() {
	const { getClosest, findHtmlEl } = pm.utils.dom;

	let doingAjax: boolean = false;

	init();

	function init() {
		document.addEventListener("click", handleEditButtonClick);
		document.addEventListener("click", handleSaveButtonClick);
		document.addEventListener("click", handleCancelButtonClick);
		document.addEventListener("dblclick", handleDocumentDblClick);
		window.addEventListener("keydown", handleDocumentKeyDown);
		window.addEventListener("keydown", handleEditorKeydown);
	}

	function handleEditButtonClick(e: MouseEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;

		const button = getClosest(target, ".inline-edit-button");
		if (!(button instanceof HTMLElement)) return;

		const block = getClosest(button, ".inline-editing");
		if (!(block instanceof HTMLElement)) return;

		openEditMode(block, button);
	}

	async function handleSaveButtonClick(e: MouseEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;

		const button = getClosest(target, ".save-button", 8);
		if (!(button instanceof HTMLButtonElement)) return;

		const block = getClosest(target, ".inline-editing");
		if (!(block instanceof HTMLElement)) return;

		await saveContent(block);
	}

	function handleCancelButtonClick(e: MouseEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;

		const button = getClosest(target, ".cancel-button", 8);
		if (!(button instanceof HTMLButtonElement)) return;

		const block = getClosest(target, ".inline-editing");
		if (!(block instanceof HTMLElement)) return;

		closeEditMode(block);
	}

	function handleDocumentDblClick(e: MouseEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;

		const contentWrapper = getClosest(target, ".is-content-editable", 8);
		if (!contentWrapper) return;

		const block = getClosest(contentWrapper, ".inline-editing");
		if (!(block instanceof HTMLElement)) return;

		const button = findHtmlEl(block, ".inline-edit-button");
		if (!(button instanceof HTMLElement)) return;

		e.preventDefault();

		openEditMode(block, button);
	}

	function handleDocumentKeyDown(e: KeyboardEvent) {
		if (!e.key) return;
		const key = e.key.toLowerCase();

		if (key === "escape" || key === "esc") {
			if (document.body.classList.contains("has-photo-popup-opened")) {
				// Wait for a 100ms, maybe other script (such as photo popup) is checking for the inline-editing state.
				setTimeout(exitAllEditModesAfterTimeout, 200);
			} else {
				exitAllEditModesExcept();
			}
		}
	}

	function exitAllEditModesAfterTimeout() {
		exitAllEditModesExcept();
	}

	async function handleEditorKeydown(e: KeyboardEvent) {
		const target = e.target;
		if (!(target instanceof Element)) return;

		const editor = getClosest(target, ".content-editor", 2);
		if (!editor) return;

		if (editor.tagName.toLocaleLowerCase() !== "input") {
			return;
		}

		const key = e.key.toLowerCase();
		if (key !== "enter") return;

		e.preventDefault();

		const block = getClosest(editor, ".inline-editing");
		if (block instanceof HTMLElement) await saveContent(block);
	}

	async function saveContent(block: HTMLElement) {
		if (doingAjax) return;
		doingAjax = true;

		const saveButton = findHtmlEl(block, ".save-button");

		const editor = findHtmlEl(block, ".content-editor");

		if (saveButton) startLoading(saveButton);

		const errorMessageEl = findHtmlEl(block, ".wpbf-notice-error");

		if (errorMessageEl) {
			errorMessageEl.classList.add("is-hidden");
			errorMessageEl.innerHTML = "";
		}

		const contentType = block.dataset.contentType ?? "";

		const commentId = contentType === "comment" ? block.dataset.commentId : 0;

		const data: SaveInlineEditingData = {
			nonce: pm.nonces.inlineEditing,
			action: "pm_inline_editing",
			post_id: block.dataset.postId ? parseInt(block.dataset.postId) : 0,
			content_type: contentType,
			content:
				editor instanceof HTMLTextAreaElement ||
				editor instanceof HTMLInputElement
					? editor.value
					: "",
			comment_id: commentId ? parseInt(commentId) : 0,
		};

		const response: SaveInlineEditingResponse = await saveInlineEditing(data);

		if (!response.success) {
			if (errorMessageEl) {
				errorMessageEl.innerHTML = response.message ? response.message : "";
				errorMessageEl.classList.remove("is-hidden");
			}

			doingAjax = false;
			if (saveButton) stopLoading(saveButton);

			return;
		}

		let editorValue =
			response.data && response.data.editor ? response.data.editor : "";
		editorValue = editorValue.replace(/\\/g, "");

		if (
			editor instanceof HTMLTextAreaElement ||
			editor instanceof HTMLInputElement
		) {
			editor.value = editorValue;
		}

		let output =
			response.data && response.data.output ? response.data.output : "";
		output = output.replace(/\\/g, "");

		const suffixEl = findHtmlEl(block, ".field-suffix");

		let emptyValue = block.dataset.emptyValue;
		emptyValue = emptyValue ? emptyValue : "";

		let formattedOutput = output;

		if (suffixEl) {
			formattedOutput = formattedOutput
				? formattedOutput + suffixEl.innerHTML
				: emptyValue;
		} else {
			formattedOutput = formattedOutput ? formattedOutput : emptyValue;
		}

		const contentWrapper = findHtmlEl(block, ".content-wrapper");

		if (contentWrapper) {
			contentWrapper.innerHTML = formattedOutput;

			if (contentType === "photo_camera" && response.data) {
				if (contentWrapper.hasAttribute("href")) {
					const cameraUrl = response.data.cameraUrl ?? "";

					contentWrapper.setAttribute("href", cameraUrl);
				}
			}
		}

		const syncToAttr = block.dataset.syncToAttr;

		if (block.dataset.syncEditorTo) {
			const selector = block.dataset.syncEditorTo;
			const syncTarget = findHtmlEl(selector);

			if (syncTarget) {
				if (syncToAttr) {
					syncTarget.setAttribute(syncToAttr, editorValue);
				} else {
					syncTarget.innerHTML = editorValue;
				}
			}
		}

		if (block.dataset.syncOutputTo) {
			const selector = block.dataset.syncOutputTo;
			const syncTarget = findHtmlEl(selector);

			if (syncTarget) {
				if (syncToAttr) {
					syncTarget.setAttribute(syncToAttr, output);
				} else {
					syncTarget.innerHTML = output;
				}
			}
		}

		closeEditMode(block);

		doingAjax = false;

		if (saveButton) stopLoading(saveButton);
	}
}
